import { AuthService } from "@cytiva-dxda/ps-microfrontend-utils";
import type { RelayInitResult } from "@cytiva-dxda/ps-context-relay";
import { loadRelay } from "./loader";
import { syncTrackingIdentity } from "./identity";

export interface RelayInitOptions {
  endpoint: string;
  activateTrackingConsent: string;
  authService: AuthService;
}

/**
 * Bootstraps the relay integration on app shell startup.
 * Uses two-phase initialization:
 * 1. Load and initialize relay (Phase 1 - setup only)
 * 2. Sync tracking identity from AuthService
 * 3. Finalize initialization (Phase 2 - enrichment, flush, hooks)
 *
 * This ensures events are enriched with correct user identity.
 * No-ops silently if the relay is disabled or if the module fails to load.
 */
export function initRelay(options: RelayInitOptions): void {
  if (!IS_CAT_ENABLED) {
    return;
  }

  loadRelay()
    .then(async (relay) => {
      if (!relay) {
        return;
      }

      const initStart = performance.now();

      let result: RelayInitResult;
      try {
        result = await relay.init({
          endpoint: options.endpoint,
          identifier: "cytiva-web",
          consent: { activateOnGroup: options.activateTrackingConsent }
        });
      } catch (err) {
        const failTime = performance.now() - initStart;
        console.error(
          `[tracking] relay.init() failed after ${failTime.toFixed(2)}ms`,
          err
        );
        if (window.DD_RUM) {
          window.DD_RUM.addError(err);
        }
        return;
      }

      // CRITICAL: Sync identity BEFORE finalizing
      try {
        await syncTrackingIdentity(options.authService);
      } catch (err) {
        console.error(
          "[tracking] identity sync failed, continuing with anonymous tracking",
          err
        );
      }

      try {
        await result.finalizeInit();
      } catch (err) {
        const finalizeTime = performance.now() - initStart;
        console.error(
          `[tracking] relay.finalizeInit() failed after ${finalizeTime.toFixed(2)}ms`,
          err
        );
        if (window.DD_RUM) {
          window.DD_RUM.addError(err);
        }
        return;
      }
    })
    .catch((err) => {
      console.error("[tracking] relay init failed:", err);
      if (window.DD_RUM) {
        window.DD_RUM.addError(err);
      }
    });
}
